import { ActionViewModel, Effector, effectorToMapKey } from "./ActionViewModel";
import { AtimInterface, HitchType, hitchTypeToJSON } from "protos/atk/enums";
import {
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  useTheme,
} from "@mui/material";
import { CarbonUser } from "common/utils/auth";
import { classes } from "common/theme/theme";
import { Field, FieldProps, useFormikContext } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { implementNameForScvInputId } from "portal/utils/atkEquipment";
import { MuiIcon } from "common/components/types";
import { ptoTypeToNameString } from "common/utils/enumTranslations";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { TypedPathTo } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import DownIcon from "common/images/atk-actions/detent-down.svg?react";
import ErrorIcon from "@mui/icons-material/PriorityHigh";
import ExtendIcon from "common/images/atk-actions/extend.svg?react";
import HoldingActionIcon from "@mui/icons-material/Repeat";
import React from "react";
import RetractIcon from "common/images/atk-actions/retract.svg?react";
import UpIcon from "common/images/atk-actions/detent-up.svg?react";

export const ActionIcon = ({
  Icon,
  isHoldingAction,
  isErrored = false,
  className,
}: {
  Icon: MuiIcon;
  isHoldingAction: boolean;
  isErrored?: boolean;
  className?: string;
}): JSX.Element => {
  const theme = useTheme();
  const iconColor = theme.palette.atkActions.iconForeground;
  const backgroundColor = theme.palette.atkActions.iconBackground;

  const mainIcon = (
    <Icon
      style={{ backgroundColor, color: iconColor }}
      className={classes("rounded-full p-2 w-12 h-12", className)}
    />
  );

  let BadgeIcon: MuiIcon | undefined;

  if (isErrored) {
    BadgeIcon = ErrorIcon;
  } else if (isHoldingAction) {
    BadgeIcon = HoldingActionIcon;
  }

  return BadgeIcon ? (
    <Badge
      // absolute positioning here makes the icon not take up space in the badge
      // layout and stretch it out into a pill shape; the badge itself is absolutely
      // positioned, so it has no other effect
      badgeContent={<BadgeIcon className="p-0 m-0 absolute text-base" />}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      color="primary"
      overlap="circular"
      className={className}
    >
      {mainIcon}
    </Badge>
  ) : (
    mainIcon
  );
};

export const StartDelayInput: React.FC<{
  path: TypedPathTo<number>;
}> = ({ path }) => {
  const { measurementSystem } = useSelf();
  const { t } = useTranslation();
  return (
    <FormikDimensionedNumberInput
      name={path.$path}
      label={t(
        "views.autotractor.autonomousActivities.action.parameterName.startDelay"
      )}
      dimensionedNumberInputProps={{
        minStoredValue: 0,
        storedUnit: "s",
        localSystem: measurementSystem,
        displayUnits: { imperial: "s", metric: "s" },
      }}
      className="w-full"
    />
  );
};

export const DirectionToggle: React.FC<{
  basePath: TypedPathTo<number>;
  variant: "scv" | "three-point";
  groupProps?: ToggleButtonGroupProps;
}> = ({ basePath, variant, ...groupProps }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  let NegativeIcon: MuiIcon;
  let PositiveIcon: MuiIcon;
  let negativeLabel: string;
  let positiveLabel: string;

  switch (variant) {
    case "scv": {
      NegativeIcon = RetractIcon;
      negativeLabel = "Retract";
      PositiveIcon = ExtendIcon;
      positiveLabel = "Extend";
      break;
    }
    case "three-point": {
      NegativeIcon = DownIcon;
      negativeLabel = "Down";
      PositiveIcon = UpIcon;
      positiveLabel = "Up";
      break;
    }
  }

  return (
    <Field name={basePath.$path}>
      {({ field }: FieldProps<number>) => (
        <ToggleButtonGroup
          orientation="horizontal"
          fullWidth
          exclusive
          value={field.value}
          onChange={(_event, newValue) => {
            setFieldValue(basePath.$path, newValue);
            setFieldTouched(basePath.$path, true);
          }}
          className="gap-2"
          {...groupProps}
        >
          <ToggleButton
            value={-1}
            className="flex-1 normal-case rounded-xl flex flex-col"
          >
            <ActionIcon Icon={NegativeIcon} isHoldingAction={false} />
            {negativeLabel}
          </ToggleButton>
          <ToggleButton
            value={1}
            className="flex-1 normal-case rounded-xl flex flex-col"
          >
            <ActionIcon Icon={PositiveIcon} isHoldingAction={false} />
            {positiveLabel}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Field>
  );
};

export function friendlyNameForEffector(
  t: TFunction,
  rig: RigEffectiveConfig,
  effector: Effector
): string {
  switch (effector.type) {
    case "engine-rpm":
    case "advanced": {
      return t(
        "views.autotractor.autonomousActivities.action.effectors.tractor"
      );
    }
    case "laserweeder":
    case "lw-estop": {
      return t(
        "views.autotractor.autonomousActivities.action.effectors.laserweeder"
      );
    }
    case "hitch": {
      if (effector.hitchType === HitchType.HITCH_TYPE_THREE_POINT_FRONT) {
        return t("models.equipment.hitchTypes.threePointFront");
      } else if (effector.hitchType === HitchType.HITCH_TYPE_THREE_POINT_REAR) {
        return t("models.equipment.hitchTypes.threePointRear");
      } else {
        return hitchTypeToJSON(effector.hitchType);
      }
    }
    case "pto": {
      return ptoTypeToNameString(t, effector.ptoType);
    }
    case "scv": {
      return (
        implementNameForScvInputId(rig, effector.scvInputId) ??
        t("views.autotractor.autonomousActivities.action.effectors.deletedScv")
      );
    }
  }
}

/**
 * An implementation of newActionAvailableForEffector that marks the action as
 * available for all effectors with the given type value.
 * @param effectorType an effector type to match
 * @returns an implementation of newActionAvailableForEffector which returns
 *   true iff the passed effector has the correct type
 */
export function allEffectorsWithType(
  effectorType: Effector["type"]
): (
  _user: CarbonUser | undefined,
  effector: Effector,
  _rigEffectiveConfig: RigEffectiveConfig
) => boolean {
  return (_user, effector, _rigEffectiveConfig) =>
    effector.type === effectorType;
}

/**
 * An implementation of newActionAvailableForEffector that marks the action as
 * available for all effectors with the given type value, if the current user
 * is a Carbon employee.
 * @param effectorType an effector type to match
 * @returns an implementation of newActionAvailableForEffector which returns
 *   true iff the passed effector has the correct type and the current user is
 *   a Carbon employee
 */
export function carbonOnlyAllEffectorsWithType(
  effectorType: Effector["type"]
): (
  user: CarbonUser | undefined,
  effector: Effector,
  _rigEffectiveConfig: RigEffectiveConfig
) => boolean {
  return (user, effector, _rigEffectiveConfig) =>
    Boolean(user?.isInternal) && effector.type === effectorType;
}

export function threePointHitchEffectors(): (
  user: CarbonUser | undefined,
  effector: Effector,
  _rigEffectiveConfig: RigEffectiveConfig
) => boolean {
  return (_user, effector, _rigEffectiveConfig) =>
    effector.type === "hitch" &&
    (effector.hitchType === HitchType.HITCH_TYPE_THREE_POINT_FRONT ||
      effector.hitchType === HitchType.HITCH_TYPE_THREE_POINT_REAR);
}

/**
 * An implementation of newActionAvailableForEffector that marks the action as
 * available for all effectors with the given type value on a LaserWeeder
 * implement with the given hitch type.
 * @param effectorType "laserweeder" or "lw-estop"
 * @param hitchType the hitch type to check for
 * @returns an implementation of newActionAvailableForEffector which returns
 *   true iff the passed effector has the correct type, the implement it
 *   refers to supports the LaserWeeder ATIM interface, and the implement is
 *   of the correct hitch type
 */
export function laserWeederEffectorsWithType(
  effectorType: "laserweeder" | "lw-estop",
  hitchType: HitchType | undefined
): (
  _user: CarbonUser | undefined,
  effector: Effector,
  _rigEffectiveConfig: RigEffectiveConfig
) => boolean {
  return (_user, effector, rigEffectiveConfig) => {
    if (effector.type !== effectorType) {
      return false;
    }

    const implement = rigEffectiveConfig.implements.find(
      (i) => i.implementId === effector.implementId
    );

    if (!implement) {
      return false;
    }

    return (
      implement.atim === AtimInterface.ATIM_LASER_WEEDER &&
      (!hitchType || implement.activeHitch?.type === hitchType)
    );
  };
}

export function positionMonitoredScvEffectors(): (
  _user: CarbonUser | undefined,
  effector: Effector,
  rigEffectiveConfig: RigEffectiveConfig
) => boolean {
  return (_user, effector, rigEffectiveConfig) => {
    if (effector.type !== "scv") {
      return false;
    }

    for (const readRegister of rigEffectiveConfig.controls?.ioLinkCfg
      ?.readRegisters ?? []) {
      if (readRegister.scvInputPosition?.scvInputId === effector.scvInputId) {
        return true;
      }
    }

    return false;
  };
}

/**
 * For a given array of action view models, returns the indexes of the actions
 * which are final on their effector.
 * @param actions the array of action view models
 */
export function effectorFinalActionIndexes(
  actions: ActionViewModel<unknown>[]
): Set<number> {
  const finalActionIndexesPerEffector = new Map<
    string,
    { actionIndex: number; startStage: number }
  >();

  for (let actionIndex = 0; actionIndex < actions.length; actionIndex++) {
    const action = actions[actionIndex]!;

    const mapKey = effectorToMapKey(action.effector);
    const existingFinalAction = finalActionIndexesPerEffector.get(mapKey) ?? {
      actionId: undefined,
      startStage: -Infinity,
    };

    if (action.startInStageIndex > existingFinalAction.startStage) {
      finalActionIndexesPerEffector.set(mapKey, {
        actionIndex,
        startStage: action.startInStageIndex,
      });
    }
  }

  // boil all of the data down into a single set for easier access
  return new Set(
    finalActionIndexesPerEffector.values().map((action) => action.actionIndex)
  );
}
